import { Component, h, Prop, State, Method, Event, EventEmitter, Element } from '@stencil/core';
declare global {
  interface Window {
    adInsights: {
      __version__: string;
      e: (category?: string, action?: string, label?: string, value?: string, version?: string, callback?: () => void) => void;
    };
  }
}

const VoiceIcon = () => (<svg viewBox="0 0 34 34" class="voice-icon">
  <line class="voice-1" stroke="currentColor" fill="none" stroke-linecap="square" x1="10.6" y1="5" x2="10.6" y2="29"/>
  <line class="voice-2" stroke="currentColor" fill="none" stroke-linecap="square" x1="17" y1="10.6" x2="17" y2="23.4"/>
  <line class="voice-3" stroke="currentColor" fill="none" stroke-linecap="square" x1="23.4" y1="7.8" x2="23.4" y2="26.2"/>
  <line class="voice-4" stroke="currentColor" fill="none" stroke-linecap="square" x1="29" y1="13.5" x2="29" y2="20.5"/>
  <line class="voice-5" stroke="currentColor" fill="none" stroke-linecap="square" x1="5" y1="13.5" x2="5" y2="20.5"/>
</svg>);

const PLAYBACK_RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];

const roundToDecimals = (value: number, decimals: number) => {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor; 
}

const AD_LOGO_BLUE = "https://assets.adsttc.com/doodles/flat/logo-white-full-with-bg.png";

@Component({
  tag: 'hd-audio-player',
  styleUrl: './hd-audio-player.pcss',
  shadow: true,
})
export class HdAudioPlayer {
  @Element() host: HTMLElement;
  /** Audio src */
  @Prop() src!: string;
  @Prop() eagerLoad: boolean = false;
  @Prop() background: string;
  @Prop({ reflect: true, mutable: true }) played: boolean = false;
  @Prop({ reflect: true, mutable: true }) isPlaying: boolean = false;
  @Prop() trackingCategory: string;
  @Prop() trackingAction: string;
  @Prop() trackingLabel: string;
  @Prop() trackingValue: string;
  /**
   * @deprecated Use audioTitle instead
   */
  @Prop() title: string = "ArchDaily Audio";
  @Prop() audioTitle: string;
  @Prop() artist: string = "ArchDaily";
  @Prop() artwork: string = AD_LOGO_BLUE;
  audioEl!: HTMLAudioElement;
  seekEl!: HTMLInputElement;
  @State() duration: number = 0;
  @State() durationFormatted: string = '0:00';
  @State() currentTime: number = 0;
  @State() currentTimeFormatted: string = '0:00';
  @State() requestAnimationFrameId: number;
  @State() playbackRate: number = 1;
  @Event({
    eventName: 'audio:playing',
    composed: true,
    bubbles: true,
  }) audioPlaying: EventEmitter<string>;
  @Event({
    eventName: 'audio:started',
    composed: true,
    bubbles: true,
  }) audioStarted: EventEmitter<Record<string, string>>;
  @Event({
    eventName: 'audio:ended',
    composed: true,
    bubbles: true,
  }) audioEnded: EventEmitter<string>;

  componentDidRender() {
    this.audioEl.addEventListener('loadedmetadata', () => {
      this.duration = this.audioEl.duration;
    });

    this.audioEl.addEventListener('ended', () => {
      this.isPlaying = false;
      this.played = true;
      this.audioEnded.emit(this.host.id ?? this.src);
      cancelAnimationFrame(this.requestAnimationFrameId);
    });

    if ("mediaSession" in navigator) {
      const paramSign = this.artwork.match(/\?/) ? '&' : '?'
      const artworkBaseUrl = this.artwork.replace('images.adsttc.com', 'snoopy.archdaily.com/images/archdaily')
      navigator.mediaSession.metadata = new MediaMetadata({
        title: this.audioTitle ?? this.title, // TODO: Update MyAD to drop title prop
        artist: this.artist,
        artwork: [
          { src: `${artworkBaseUrl}${paramSign}format=png&width=96&height=96&crop=true`, sizes: '96x96', type: 'image/png' },
          { src: `${artworkBaseUrl}${paramSign}format=png&width=128&height=128&crop=true`, sizes: '128x128', type: 'image/png' },
          { src: `${artworkBaseUrl}${paramSign}format=png&width=256&height=256&crop=true`, sizes: '256x256', type: 'image/png' },
          { src: `${artworkBaseUrl}${paramSign}format=png&width=512&height=512&crop=true`, sizes: '512x512', type: 'image/png' },
        ]
      });
      navigator.mediaSession.setActionHandler('play', this.playAudio);
      navigator.mediaSession.setActionHandler('pause', this.playAudio);
    }
  }

  private formatTime = (durationSecs: number) => {
    const minutes = Math.floor(durationSecs / 60);
    const seconds = Math.floor(durationSecs % 60);
    const returnedSeconds = seconds < 10 ? `0${seconds}` : seconds;
    return `${minutes}:${returnedSeconds}`;
  }

  private whilePlaying = () => {
    if (this.seekEl === undefined) return;
    this.seekEl.value = Math.ceil(this.audioEl.currentTime).toString();
    this.currentTimeFormatted = this.formatTime(this.audioEl.currentTime);

    this.seekEl.style.setProperty('--seek-before-width', `${this.seekEl.valueAsNumber / parseInt(this.seekEl.max) * 100}%`);
    this.requestAnimationFrameId = requestAnimationFrame(this.whilePlaying);
  }

  private playAudio = () => {
    if (this.isPlaying) {
      this.audioEl.pause();
      navigator.mediaSession.playbackState = "paused";
      this.isPlaying = false;
      cancelAnimationFrame(this.requestAnimationFrameId);
      return;
    }
    this.audioEl.play();

    if (this.audioEl.currentTime === 0) {
      const tracking = {
        category: this.trackingCategory,
        action: this.trackingAction,
        label: this.trackingLabel,
        value: this.trackingValue ?? ''
      }
      this.audioStarted.emit(tracking);
    }

    this.trackEvent('play');
    navigator.mediaSession.playbackState = "playing";
    this.isPlaying = true;
    requestAnimationFrame(this.whilePlaying);
    this.audioPlaying.emit(this.host.id ?? this.src);
  }

  @Method()
  async play (delay: number = 0) {
    setTimeout(() => {
      this.playAudio();
      return true;
    }, delay)
  }

  @Method()
  async pause () {
    this.audioEl.pause();
    this.isPlaying = false;
    cancelAnimationFrame(this.requestAnimationFrameId);
    return true;
  }

  private onSeekInput = () => {
    this.currentTimeFormatted = this.formatTime(parseInt(this.seekEl.value));
  
    if (!this.audioEl.paused) {
      cancelAnimationFrame(this.requestAnimationFrameId);
    }
  }

  private onSeekChange = (e: Event) => {
    this.currentTime = parseInt((e.target as HTMLInputElement).value);
    this.audioEl.currentTime = this.currentTime;
    this.seekEl.style.setProperty('--seek-before-width', `${roundToDecimals(this.seekEl.valueAsNumber / parseInt(this.seekEl.max) * 100, 1)}%`);
    if (!this.audioEl.paused) {
      requestAnimationFrame(this.whilePlaying);
    }
  }

  private skipTo = (newTime: number) => {
    this.currentTime = newTime;
    this.audioEl.currentTime = newTime;
    this.seekEl.value = newTime.toString();
    this.seekEl.style.setProperty('--seek-before-width', `${roundToDecimals(this.seekEl.valueAsNumber / parseInt(this.seekEl.max) * 100, 1)}%`);
    if (!this.isPlaying) {
      this.playAudio();
    }
  }

  private rewind = (secs: number = 10) => {
    const newTime = Math.max(this.currentTime - secs, 0)
    this.skipTo(newTime);
    this.trackEvent('rewind');
  }

  private fastForward = (secs: number = 15) => {
    const newTime = Math.min(this.currentTime + secs, this.duration);
    this.skipTo(newTime);
    this.trackEvent('fast-forward');
  }

  private updatePlaybackRate = () => {
    const currentIndex = PLAYBACK_RATES.indexOf(this.playbackRate);
    const newIndex = (currentIndex + 1) % PLAYBACK_RATES.length;
    this.playbackRate = PLAYBACK_RATES[newIndex];
    this.audioEl.playbackRate = PLAYBACK_RATES[newIndex];
    this.trackEvent('playback-rate-change');
  }

  private trackEvent(action: string) {
    if (!window.adInsights || !window.adInsights.__version__) return;
    if (!this.trackingCategory || !this.trackingValue) return;
  
    window.adInsights.e(this.trackingCategory, action, this.trackingValue);
  }

  render() {
    return (
      <div class={`container ${this.isPlaying ? "playing": ""}`} style={{ background: this.background ?? "inherit" }}>
        <div class="controls-row">
          <div class="left-controls">
            <VoiceIcon></VoiceIcon>
            {this.played ? <hd-icon class="played-icon" svg="checkmark_1_small" width="34" height="34" /> : null}
          </div>
          <div class="controls">
            <button onClick={() => this.rewind()}>
              <hd-icon svg="rewind_10" width="32" height="32" />
            </button>
            <button class="play" onClick={this.playAudio}>
              <hd-icon class="pause" svg="pause" width="40" height="40" />
              <hd-icon class="play" svg="play_circle" width="40" height="40" />
            </button>
            <button onClick={() => this.fastForward()}>
              <hd-icon svg="fast_forward15" width="32" height="32" />
            </button>
            <button class="playback-rate" onClick={() => this.updatePlaybackRate()}>
              <span>{this.playbackRate}</span>x
            </button>
          </div>
        </div>

        <div class="progress">
          <span class="time" id="current-time">{this.currentTimeFormatted}</span>
          <input ref={el => this.seekEl = el as HTMLInputElement} class="progress" type="range" id="seek"
            min="0" max={Math.round(this.duration)} value={this.currentTime}
            onChange={this.onSeekChange} onInput={this.onSeekInput} />
          <span class="time" id="duration">{this.formatTime(this.duration)}</span>
        </div>
        <audio ref={el => (this.audioEl = el as HTMLAudioElement)} preload={this.eagerLoad ? "auto" : "metadata"}>
          <source src={this.src} type="audio/mpeg" />
          Your browser does not support the audio element.
        </audio>
      </div>
    );
  }
}
